import { Box, ButtonBase, Chip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { primitives } from '@/styles/tokens/primitives';
import type { ActivityTimelineEntryVariant } from './types';

export const PanelRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
}));

export const PanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  height: 40,
  padding: theme.spacing(1, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

export const PanelHeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const PanelBody = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

export const TimelineList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const TimelineItemRoot = styled(Box, {
  shouldForwardProp: (p) => p !== 'highlighted' && p !== 'variant',
})<{ highlighted?: boolean; variant?: ActivityTimelineEntryVariant }>(
  ({ theme, highlighted, variant = 'event' }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    padding:
      variant === 'event' ? theme.spacing(1, 1.25, 1, 0.5) : theme.spacing(0.75, 1.25, 0.75, 0.5),
    borderRadius: 10,
    minHeight: variant === 'event' ? 40 : 32,
    ...(highlighted
      ? {
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
        }
      : {}),
  }),
);

/** Left rail: dashed connectors + dot */
export const TimelineTrack = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: 24,
  flexShrink: 0,
});

export const TimelineConnector = styled(Box, {
  shouldForwardProp: (p) => p !== 'visible' && p !== 'connectorPosition',
})<{ visible?: boolean; connectorPosition: 'top' | 'bottom' }>(
  ({ theme, visible, connectorPosition }) => ({
    position: 'absolute',
    ...(connectorPosition === 'top' && { top: 0, transform: 'translateY(-100%)' }),
    ...(connectorPosition === 'bottom' && { bottom: 0, transform: 'translateY(100%)' }),
    width: 0,
    height: '100%',
    borderLeft: visible ? `1px dashed ${theme.palette.divider}` : 'none',
    marginLeft: '1px',
  }),
);

export const TimelineDot = styled(Box, {
  shouldForwardProp: (p) => p !== 'variant',
})<{ variant?: ActivityTimelineEntryVariant }>(({ theme, variant = 'event' }) => ({
  width: variant === 'event' ? 24 : 16,
  height: variant === 'event' ? 24 : 16,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: variant === 'event' ? theme.palette.grey[100] : 'transparent',
  border: 'none',
  color: theme.palette.grey[50],
  zIndex: 1,
}));

export const TimelineMain = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const TimelineTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
}));

export const TimelineTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  fontSize: 12,
  gap: theme.spacing(0.75),
}));

export const TimelineDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const TimelineActionButton = styled(ButtonBase)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: primitives.brand[120],
  marginTop: theme.spacing(0.5),
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
}));

export const TimelineEmptyState = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 1),
}));

export const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },
}));
